import React, { useState, useEffect } from 'react';
import { masterSupabase } from '../lib/supabase-master.js';
import { 
  Gauge, 
  Droplets, 
  Flame, 
  Zap, 
  TrendingUp, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  Activity,
  Eye,
  Edit,
  Filter,
  Search,
  Thermometer
, X, Plus, Calendar, FileText, Save, Trash2 } from 'lucide-react';

const MeterDashboard = ({ propertyId, theme }) => {
  const [activeTab, setActiveTab] = useState('meters');
  const [meterStats, setMeterStats] = useState(null);
  const [meterPoints, setMeterPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [meterReadings, setMeterReadings] = useState({});
  const [readingDate, setReadingDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingReadings, setSavingReadings] = useState(false);
  const [bulkFilterType, setBulkFilterType] = useState('all');
  const [showMeterModal, setShowMeterModal] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [meterFormData, setMeterFormData] = useState({
    meter_kind: 'electricity',
    external_id: '',
    unit_id: null,
    location_description: '',
    provider_name: '',
    provider_serial_number: '',
    manufacturer: '',
    model: '',
    installation_date: '',
    device_status: 'active',
    is_active: true,
    provider_specific_data: { tenant: '', contract_account: '', unit: '' }
  });
  const [savingMeter, setSavingMeter] = useState(false);
  const [meterInsights, setMeterInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);


  // Load data when propertyId changes
  useEffect(() => {
    if (propertyId) {
      loadMeterData();
    } else {
      // Wait for property selection
      setError('Bitte wählen Sie eine Immobilie aus');
    }
  }, [propertyId]);

  const loadMeterData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load meter points
      const { data: meters, error: metersError } = await masterSupabase
        .from('meter_points')
        .select('*')
        .eq('property_id', propertyId)
        .eq('is_active', true)
        .order('location_description');

      if (metersError) throw metersError;

      setMeterPoints(meters || []);

      // Calculate stats
      const stats = {
        total_devices: meters?.length || 0,
        active_devices: meters?.filter(m => m.device_status === 'active').length || 0,
        warning_devices: 0,
        primary_meter_provider: meters?.[0]?.provider_name || 'Nicht konfiguriert'
      };

      setMeterStats(stats);
    } catch (err) {
      console.error('Error loading meter data:', err);
      setError('Fehler beim Laden der Zählerdaten: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadMeterData();
    setRefreshing(false);
  };

  // Load meter readings
  const loadMeterReadings = async (meterId) => {
    try {
      const { data, error } = await masterSupabase
        .from('meter_readings')
        .select('*')
        .eq('meter_point_id', meterId)
        .order('reading_date', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error loading readings:', err);
      return [];
    }
  };

  // Open details modal
  const openDetailsModal = async (meter) => {
    setSelectedMeter(meter);
    setShowDetailsModal(true);
    setLoadingInsights(true);
    
    // Load readings for this meter
    const readings = await loadMeterReadings(meter.id);
    setMeterReadings(prev => ({ ...prev, [meter.id]: readings }));
    
    // Load insights data (unit info, tenant info, etc.)
    await loadMeterInsights(meter);
  };
  
  // Load insights for a meter
  const loadMeterInsights = async (meter) => {
    try {
      setLoadingInsights(true);
      
      const insights = {
        unit: null,
        meterAge: null,
        lastReading: null
      };
      
      // Load unit data if unit_id exists
      if (meter.unit_id) {
        const { data: unitData, error: unitError } = await masterSupabase
          .from('units')
          .select('*')
          .eq('id', meter.unit_id)
          .single();
        
        if (!unitError && unitData) {
          insights.unit = unitData;
        }
      }
      
      // Calculate meter age from installation_date
      if (meter.provider_specific_data?.installation_date) {
        const installDate = new Date(meter.provider_specific_data.installation_date);
        const now = new Date();
        const ageYears = (now - installDate) / (1000 * 60 * 60 * 24 * 365);
        insights.meterAge = ageYears.toFixed(1);
      }
      
      // Get last reading
      const { data: lastReadingData, error: readingError } = await masterSupabase
        .from('meter_readings')
        .select('*')
        .eq('meter_point_id', meter.id)
        .order('reading_date', { ascending: false })
        .limit(1);
      
      if (!readingError && lastReadingData && lastReadingData.length > 0) {
        insights.lastReading = lastReadingData[0];
      }
      
      setMeterInsights(insights);
    } catch (err) {
      console.error('Error loading insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Open meter modal for add/edit
  const openMeterModal = (meter = null) => {
    if (meter) {
      // Edit mode
      setEditingMeter(meter);
      setMeterFormData({
        meter_kind: meter.meter_kind || 'electricity',
        external_id: meter.external_id || '',
        unit_id: meter.unit_id || null,
        location_description: meter.location_description || '',
        provider_name: meter.provider_name || '',
        provider_serial_number: meter.provider_serial_number || '',
        manufacturer: meter.provider_specific_data?.manufacturer || '',
        model: meter.provider_specific_data?.model || '',
        installation_date: meter.provider_specific_data?.installation_date || '',
        device_status: meter.device_status || 'active',
        is_active: meter.is_active !== false,
        provider_specific_data: {
          tenant: meter.provider_specific_data?.tenant || '',
          contract_account: meter.provider_specific_data?.contract_account || '',
          unit: meter.provider_specific_data?.unit || ''
        }
      });
    } else {
      // Add mode
      setEditingMeter(null);
      setMeterFormData({
        meter_kind: 'electricity',
        external_id: '',
        unit_id: null,
        location_description: '',
        provider_name: '',
        provider_serial_number: '',
        manufacturer: '',
        model: '',
        installation_date: '',
        device_status: 'active',
        is_active: true,
        provider_specific_data: { tenant: '', contract_account: '', unit: '' }
      });
    }
    setShowMeterModal(true);
  };

  // Handle meter form save
  const handleMeterSave = async () => {
    setSavingMeter(true);
    
    try {
      // Validate required fields
      if (!meterFormData.external_id || !meterFormData.meter_kind) {
        alert('Bitte füllen Sie alle Pflichtfelder aus.');
        setSavingMeter(false);
        return;
      }

      // Prepare data
      const meterData = {
        property_id: propertyId,
        meter_kind: meterFormData.meter_kind,
        external_id: meterFormData.external_id,
        unit_id: meterFormData.unit_id || null,
        location_description: meterFormData.location_description,
        provider_name: meterFormData.provider_name,
        provider_serial_number: meterFormData.provider_serial_number,
        device_status: meterFormData.device_status,
        is_active: meterFormData.is_active,
        provider_specific_data: {
          ...meterFormData.provider_specific_data,
          manufacturer: meterFormData.manufacturer,
          model: meterFormData.model,
          installation_date: meterFormData.installation_date
        },
        created_by: '9c1e494f-72f1-4f33-b878-d51140936704' // TODO: Get from auth context
      };

      if (editingMeter) {
        // Update existing meter
        const { error } = await masterSupabase
          .from('meter_points')
          .update(meterData)
          .eq('id', editingMeter.id);
        
        if (error) throw error;
        alert('Zähler erfolgreich aktualisiert!');
      } else {
        // Create new meter
        const { error } = await masterSupabase
          .from('meter_points')
          .insert(meterData);
        
        if (error) throw error;
        alert('Zähler erfolgreich hinzugefügt!');
      }

      setShowMeterModal(false);
      loadMeterData(); // Reload data
      
    } catch (err) {
      console.error('Error saving meter:', err);
      alert('Fehler beim Speichern des Zählers: ' + err.message);
    } finally {
      setSavingMeter(false);
    }
  };

  // Handle meter delete
  const handleMeterDelete = async (meterId) => {
    if (!confirm('Möchten Sie diesen Zähler wirklich löschen?')) {
      return;
    }

    try {
      const { error } = await masterSupabase
        .from('meter_points')
        .delete()
        .eq('id', meterId);
      
      if (error) throw error;
      
      alert('Zähler erfolgreich gelöscht!');
      loadMeterData(); // Reload data
      
    } catch (err) {
      console.error('Error deleting meter:', err);
      alert('Fehler beim Löschen des Zählers: ' + err.message);
    }
  };


  // Save new reading
  const saveReading = async (meterId, value) => {
    try {
      const { error } = await masterSupabase
        .from('meter_readings')
        .insert({
          meter_point_id: meterId,
          reading_date: readingDate,
          reading_value: parseFloat(value),
          reading_type: 'manual',
          created_by: '9c1e494f-72f1-4f33-b878-d51140936704' // TODO: Get from auth context
        });
      
      if (error) throw error;
      
      // Reload readings
      const readings = await loadMeterReadings(meterId);
      setMeterReadings(prev => ({ ...prev, [meterId]: readings }));
      
      return true;
    } catch (err) {
      console.error('Error saving reading:', err);
      return false;
    }
  };

  // Handle bulk reading save
  const handleBulkReadingSave = async () => {
    setSavingReadings(true);
    
    try {
      const entries = Object.entries(meterReadings)
        .filter(([_, value]) => value && value.newReading)
        .map(([meterId, data]) => ({
          meter_point_id: meterId,
          reading_date: readingDate,
          reading_value: parseFloat(data.newReading),
          reading_type: 'manual',
          created_by: '9c1e494f-72f1-4f33-b878-d51140936704' // TODO: Get from auth context
        }));
      
      if (entries.length === 0) {
        alert('Bitte geben Sie mindestens einen Zählerstand ein.');
        setSavingReadings(false);
        return;
      }
      
      const { error } = await masterSupabase
        .from('meter_readings')
        .insert(entries);
      
      if (error) throw error;
      
      alert(`${entries.length} Zählerstände erfolgreich gespeichert!`);
      setShowReadingModal(false);
      setMeterReadings({});
      
    } catch (err) {
      console.error('Error saving bulk readings:', err);
      alert('Fehler beim Speichern der Zählerstände.');
    } finally {
      setSavingReadings(false);
    }
  };

  const getMeterIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'electricity': return <Zap className="w-5 h-5" />;
      case 'water_cold': 
      case 'water_hot':
      case 'water': return <Droplets className="w-5 h-5" />;
      case 'gas': return <Flame className="w-5 h-5" />;
      case 'heat':
      case 'heating': return <Thermometer className="w-5 h-5" />;
      case 'smoke_detector': return <AlertTriangle className="w-5 h-5" />;
      default: return <Gauge className="w-5 h-5" />;
    }
  };

  const getMeterTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'electricity': return 'text-yellow-600';
      case 'water_cold': 
      case 'water_hot':
      case 'water': return 'text-blue-600';
      case 'gas': return 'text-orange-600';
      case 'heat':
      case 'heating': return 'text-red-600';
      case 'smoke_detector': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getMeterTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'electricity': return 'Strom';
      case 'water_cold': return 'Kaltwasser';
      case 'water_hot': return 'Warmwasser';
      case 'water': return 'Wasser';
      case 'gas': return 'Gas';
      case 'heat': return 'Fernwärme';
      case 'heating': return 'Heizung';
      case 'smoke_detector': return 'Rauchmelder';
      default: return type;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  const filteredMeterPoints = meterPoints.filter(meter => {
    const searchableText = `${meter.location_description || ''} ${meter.external_id || ''} ${meter.provider_specific_data?.unit || ''} ${meter.provider_specific_data?.tenant || ''}`.toLowerCase();
    const matchesSearch = searchableText.includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || meter.meter_kind === filterType;
    return matchesSearch && matchesFilter;
  });

  if (loading) {

  // Meter Management Functions
  const openMeterModal = (meter = null) => {
    if (meter) {
      setEditingMeter(meter);
      setMeterFormData({
        meter_kind: meter.meter_kind || 'electricity',
        external_id: meter.external_id || '',
        unit_id: meter.unit_id || null,
        location_description: meter.location_description || '',
        provider_name: meter.provider_name || '',
        provider_serial_number: meter.provider_serial_number || '',
        manufacturer: meter.manufacturer || '',
        model: meter.model || '',
        installation_date: meter.installation_date || '',
        device_status: meter.device_status || 'active',
        is_active: meter.is_active !== false,
        provider_specific_data: meter.provider_specific_data || { tenant: '', contract_account: '', unit: '' }
      });
    } else {
      setEditingMeter(null);
      setMeterFormData({
        meter_kind: 'electricity',
        external_id: '',
        unit_id: null,
        location_description: '',
        provider_name: '',
        provider_serial_number: '',
        manufacturer: '',
        model: '',
        installation_date: '',
        device_status: 'active',
        is_active: true,
        provider_specific_data: { tenant: '', contract_account: '', unit: '' }
      });
    }
    setShowMeterModal(true);
  };

  const handleMeterSave = async () => {
    if (!propertyId) {
      alert('Bitte wählen Sie zuerst eine Property aus');
      return;
    }
    if (!meterFormData.meter_kind) {
      alert('Bitte wählen Sie einen Zählertyp aus');
      return;
    }

    setSavingMeter(true);
    try {
      const user = JSON.parse(localStorage.getItem('sb-ufzglwnuetkvchzjuxdg-auth-token'))?.user;
      if (!user) throw new Error('Not authenticated');

      const meterData = {
        created_by: user.id,
        property_id: propertyId,
        meter_kind: meterFormData.meter_kind,
        external_id: meterFormData.external_id || null,
        unit_id: meterFormData.unit_id || null,
        location_description: meterFormData.location_description || null,
        provider_name: meterFormData.provider_name || null,
        provider_serial_number: meterFormData.provider_serial_number || null,
        manufacturer: meterFormData.manufacturer || null,
        model: meterFormData.model || null,
        installation_date: meterFormData.installation_date || null,
        device_status: meterFormData.device_status,
        is_active: meterFormData.is_active,
        provider_specific_data: meterFormData.provider_specific_data
      };

      if (editingMeter) {
        const { error } = await masterSupabase
          .from('meter_points')
          .update(meterData)
          .eq('id', editingMeter.id);
        if (error) throw error;
        alert('Zähler erfolgreich aktualisiert!');
      } else {
        const { error } = await masterSupabase
          .from('meter_points')
          .insert([meterData]);
        if (error) throw error;
        alert('Zähler erfolgreich hinzugefügt!');
      }

      setShowMeterModal(false);
      loadMeterPoints();
    } catch (error) {
      console.error('Error saving meter:', error);
      alert('Fehler beim Speichern: ' + error.message);
    } finally {
      setSavingMeter(false);
    }
  };

  const handleMeterDelete = async (meterId) => {
    if (!confirm('Möchten Sie diesen Zähler wirklich löschen?')) return;
    try {
      const { error } = await masterSupabase
        .from('meter_points')
        .delete()
        .eq('id', meterId);
      if (error) throw error;
      alert('Zähler erfolgreich gelöscht!');
      loadMeterPoints();
    } catch (error) {
      console.error('Error deleting meter:', error);
      alert('Fehler beim Löschen: ' + error.message);
    }
  };

  const loadMeterInsights = async (meter) => {
    setLoadingInsights(true);
    try {
      // Load unit info
      let unitInfo = null;
      if (meter.unit_id) {
        const { data: unitData } = await masterSupabase
          .from('units')
          .select('*, tenants(*)')
          .eq('id', meter.unit_id)
          .single();
        unitInfo = unitData;
      }

      // Load readings for this meter
      const { data: readings } = await masterSupabase
        .from('meter_readings')
        .select('*')
        .eq('meter_point_id', meter.id)
        .order('reading_date', { ascending: false })
        .limit(10);

      // Calculate age
      let meterAge = null;
      if (meter.installation_date) {
        const installDate = new Date(meter.installation_date);
        const now = new Date();
        const years = Math.floor((now - installDate) / (365.25 * 24 * 60 * 60 * 1000));
        const months = Math.floor(((now - installDate) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
        meterAge = { years, months };
      }

      // Last reading date
      let lastReadingDays = null;
      if (readings && readings.length > 0) {
        const lastReading = new Date(readings[0].reading_date);
        const now = new Date();
        lastReadingDays = Math.floor((now - lastReading) / (24 * 60 * 60 * 1000));
      }

      setMeterInsights({
        unitInfo,
        readings: readings || [],
        meterAge,
        lastReadingDays
      });
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Lade Zählerdaten...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }


  return (
    <div className={`p-6 ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Gauge className="w-7 h-7 mr-2 text-blue-600" />
            ClaraMeter Dashboard
          </h2>
          <p className="text-gray-600 mt-1">Energie- & Verbrauchsüberwachung</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
              <button
                onClick={() => setShowReadingModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Zählerablesung
              </button>
              <button
                onClick={() => openMeterModal()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Zähler hinzufügen
              </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {meterStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gesamt Zähler</p>
                <p className="text-2xl font-bold">{meterStats.total_devices || 0}</p>
              </div>
              <Gauge className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aktive Zähler</p>
                <p className="text-2xl font-bold text-green-600">{meterStats.active_devices || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Warnungen</p>
                <p className="text-2xl font-bold text-yellow-600">{meterStats.warning_devices || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Hauptanbieter</p>
                <p className="text-sm font-medium">{meterStats.primary_meter_provider}</p>
              </div>
              <Gauge className="w-8 h-8 text-gray-600" />
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-6">
        {[
          { id: 'overview', label: 'Übersicht', icon: BarChart3 },
          { id: 'meters', label: 'Zähler', icon: Gauge },
          { id: 'readings', label: 'Ablesungen', icon: Activity },
          { id: 'analytics', label: 'Analysen', icon: TrendingUp }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : theme === 'dark'
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'meters' && (
        <div>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Zähler suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-700 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={`px-3 py-2 border rounded-lg ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">Alle Typen</option>
                <option value="electricity">Strom</option>
                <option value="water_cold">Kaltwasser</option>
                <option value="water_hot">Warmwasser</option>
                <option value="gas">Gas</option>
                <option value="heat">Fernwärme</option>
              </select>
            </div>
          </div>

          {/* Meters Grid */}
          {filteredMeterPoints.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMeterPoints.filter(meter => bulkFilterType === 'all' || meter.meter_kind === bulkFilterType || (bulkFilterType === 'water_cold' && (meter.meter_kind === 'water_cold' || meter.meter_kind === 'water_hot'))).map((meter) => (
                <div
                  key={meter.id}
                  className={`p-4 rounded-lg border hover:shadow-lg transition-shadow ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg ${getMeterTypeColor(meter.meter_kind)} bg-opacity-10`}>
                        {getMeterIcon(meter.meter_kind)}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-sm">{meter.location_description || `Zähler ${meter.external_id}`}</h3>
                        <p className="text-xs text-gray-600">{getMeterTypeLabel(meter.meter_kind)}</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs flex items-center ${getStatusColor(meter.device_status)}`}>
                      {getStatusIcon(meter.device_status)}
                      <span className="ml-1 capitalize">{meter.device_status === 'active' ? 'Aktiv' : meter.device_status}</span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Zählernummer:</span>
                      <span className="font-mono text-xs">{meter.external_id}</span>
                    </div>
                    {meter.provider_specific_data?.unit && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Einheit:</span>
                        <span>{meter.provider_specific_data.unit}</span>
                      </div>
                    )}
                    {meter.provider_specific_data?.tenant && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mieter:</span>
                        <span className="text-xs">{meter.provider_specific_data.tenant}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Anbieter:</span>
                      <span>{meter.provider_name || 'Nicht angegeben'}</span>
                    </div>
                    {meter.provider_specific_data?.contract_account && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vertragskonto:</span>
                        <span className="font-mono text-xs">{meter.provider_specific_data.contract_account}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2 mt-4">
                    <button 
                      onClick={() => openDetailsModal(meter)}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Details
                    </button>
                    <button 
                      onClick={() => openMeterModal(meter)}
                      className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      title="Bearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleMeterDelete(meter.id)}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-12 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg`}>
              <Gauge className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Keine Zähler gefunden</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filterType !== 'all' 
                  ? 'Keine Zähler entsprechen Ihren Suchkriterien.' 
                  : 'Für diese Immobilie wurden noch keine Zähler konfiguriert.'
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Other tab contents */}
      {activeTab === 'overview' && (
        <div className={`p-8 text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg`}>
          <BarChart3 className="w-16 h-16 mx-auto text-blue-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Übersicht Dashboard</h3>
          <p className="text-gray-600">Umfassende Energieverbrauchsübersicht in Entwicklung...</p>
        </div>
      )}

      {activeTab === 'readings' && (
        <div className={`p-8 text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg`}>
          <Activity className="w-16 h-16 mx-auto text-green-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Zählerablesungen</h3>
          <p className="text-gray-600">Historische und Echtzeit-Ablesungen in Entwicklung...</p>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className={`p-8 text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg`}>
          <TrendingUp className="w-16 h-16 mx-auto text-purple-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Analysen & Trends</h3>
          <p className="text-gray-600">Verbrauchsanalysen und Prognosen in Entwicklung...</p>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedMeter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto`}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Zähler Details</h3>
                <p className="text-sm opacity-90">{selectedMeter.external_id}</p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-1 hover:bg-blue-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content - 2 Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
              {/* Left Column - Main Details (2/3 width) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Meter Info */}
                <div>
                  <h4 className={`font-semibold mb-3 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <FileText className="w-4 h-4 mr-2" />
                    Zählerinformationen
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Zählernummer:</span>
                      <p className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedMeter.external_id}</p>
                    </div>
                    <div>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Typ:</span>
                      <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedMeter.meter_kind === 'electricity' ? 'Strom' : 
                          selectedMeter.meter_kind === 'heat' ? 'Wärme' : 
                          selectedMeter.meter_kind === 'water_cold' ? 'Kaltwasser' :
                          selectedMeter.meter_kind === 'water_hot' ? 'Warmwasser' :
                          selectedMeter.meter_kind === 'gas' ? 'Gas' : selectedMeter.meter_kind}</p>
                    </div>
                    <div>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Standort:</span>
                      <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedMeter.location_description || 'Nicht angegeben'}</p>
                    </div>
                    <div>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Anbieter:</span>
                      <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedMeter.provider_name || 'Nicht angegeben'}</p>
                    </div>
                    {selectedMeter.unit_identifier && (
                      <div>
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Einheit:</span>
                        <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedMeter.unit_identifier}</p>
                      </div>
                    )}
                    {selectedMeter.provider_specific_data?.tenant && (
                      <div>
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mieter:</span>
                        <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedMeter.provider_specific_data.tenant}</p>
                      </div>
                    )}
                    <div>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Status:</span>
                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                        selectedMeter.device_status === 'active' ? 'bg-green-100 text-green-800' :
                        selectedMeter.device_status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedMeter.device_status === 'active' ? 'Aktiv' :
                         selectedMeter.device_status === 'warning' ? 'Warnung' :
                         selectedMeter.device_status === 'error' ? 'Fehler' : 'Inaktiv'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* New Reading Input */}
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <h4 className={`font-semibold mb-3 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <Plus className="w-4 h-4 mr-2" />
                    Neuer Zählerstand
                  </h4>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className={`block text-sm mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Datum</label>
                      <input
                        type="date"
                        value={readingDate}
                        onChange={(e) => setReadingDate(e.target.value)}
                        className={`w-full px-3 py-2 rounded border ${
                          theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-sm mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Zählerstand</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Wert eingeben"
                        id={`reading-input-${selectedMeter.id}`}
                        className={`w-full px-3 py-2 rounded border ${
                          theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const value = e.target.value;
                            if (value) {
                              saveReading(selectedMeter.id, value);
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={async () => {
                          const input = document.getElementById(`reading-input-${selectedMeter.id}`);
                          if (input.value) {
                            const success = await saveReading(selectedMeter.id, input.value);
                            if (success) {
                              input.value = '';
                              alert('Zählerstand gespeichert!');
                            }
                          }
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reading History */}
                <div>
                  <h4 className={`font-semibold mb-3 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Zählerstand-Historie
                  </h4>
                  {meterReadings[selectedMeter.id] && meterReadings[selectedMeter.id].length > 0 ? (
                    <div className="space-y-2">
                      {meterReadings[selectedMeter.id].map((reading, idx) => (
                        <div
                          key={idx}
                          className={`flex justify-between items-center p-3 rounded ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center">
                            <Calendar className={`w-4 h-4 mr-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{new Date(reading.reading_date).toLocaleDateString('de-DE')}</span>
                          </div>
                          <span className={`font-mono font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{reading.reading_value.toLocaleString('de-DE')} {
                            selectedMeter.meter_kind === 'electricity' ? 'kWh' :
                            selectedMeter.meter_kind === 'gas' ? 'm³' :
                            selectedMeter.meter_kind === 'heat' ? 'kWh' : 'm³'
                          }</span>
                          <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{reading.reading_type}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Keine Zählerstände vorhanden</p>
                  )}
                </div>
              </div>

              {/* Right Column - Insights Panel (1/3 width) */}
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <h4 className={`font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <Activity className="w-4 h-4 mr-2" />
                    Insights
                  </h4>

                  {loadingInsights ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Unit Info */}
                      {meterInsights?.unit ? (
                        <div className={`p-3 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-white'}`}>
                          <h5 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Einheit</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Nummer:</span>
                              <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{meterInsights.unit.unit_number}</span>
                            </div>
                            {meterInsights.unit.size_sqm && (
                              <div className="flex justify-between">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Größe:</span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{meterInsights.unit.size_sqm} m²</span>
                              </div>
                            )}
                            {meterInsights.unit.rooms && (
                              <div className="flex justify-between">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Zimmer:</span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{meterInsights.unit.rooms}</span>
                              </div>
                            )}
                            {meterInsights.unit.tenant_name && (
                              <div className="mt-2 pt-2 border-t border-gray-300">
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mieter:</span>
                                <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{meterInsights.unit.tenant_name}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`p-3 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-white'}`}>
                          <h5 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Einheit</h5>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Keine Einheit zugeordnet</p>
                        </div>
                      )}

                      {/* Meter Age */}
                      <div className={`p-3 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-white'}`}>
                        <h5 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Zähleralter</h5>
                        {meterInsights?.meterAge ? (
                          <div className="text-center">
                            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>{meterInsights.meterAge}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Jahre</p>
                          </div>
                        ) : (
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Installationsdatum unbekannt</p>
                        )}
                      </div>

                      {/* Last Reading */}
                      <div className={`p-3 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-white'}`}>
                        <h5 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Letzte Ablesung</h5>
                        {meterInsights?.lastReading ? (
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Datum:</span>
                              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{new Date(meterInsights.lastReading.reading_date).toLocaleDateString('de-DE')}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Wert:</span>
                              <span className={`font-mono font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{meterInsights.lastReading.reading_value.toLocaleString('de-DE')}</span>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Keine Ablesungen vorhanden</p>
                        )}
                      </div>

                      {/* Placeholder for future features */}
                      <div className={`p-3 rounded border-2 border-dashed ${theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                        <h5 className={`text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Zukünftige Features</h5>
                        <ul className={`text-xs space-y-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          <li>• Verbrauchsranking</li>
                          <li>• Kostenprognose</li>
                          <li>• Wartungshinweise</li>
                          <li>• Verbrauchstrends</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Bulk Reading Modal */}
      {showReadingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-green-600 text-white p-4 rounded-t-lg flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Zählerablesung</h3>
                <p className="text-sm opacity-90">Zählerstände für {filteredMeterPoints.length} Zähler erfassen</p>
              </div>
              <button
                onClick={() => setShowReadingModal(false)}
                className="p-1 hover:bg-green-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Date Selection */}
            <div className="p-4 border-b">
              <label className="block text-sm font-medium mb-2">Ablesedatum</label>
              <input
                type="date"
                value={readingDate}
                onChange={(e) => setReadingDate(e.target.value)}
                className={`px-3 py-2 rounded border ${
                  theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                }`}
              />
            </div>

            {/* Meters List */}
            {/* Filter Chips */}
            <div className="p-4 border-b">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setBulkFilterType('all')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    bulkFilterType === 'all'
                      ? 'bg-blue-600 text-white'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Alle ({filteredMeterPoints.length})
                </button>
                <button
                  onClick={() => setBulkFilterType('electricity')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                    bulkFilterType === 'electricity'
                      ? 'bg-yellow-600 text-white'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  Strom ({filteredMeterPoints.filter(m => m.meter_kind === 'electricity').length})
                </button>
                <button
                  onClick={() => setBulkFilterType('water_cold')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                    bulkFilterType === 'water_cold'
                      ? 'bg-blue-600 text-white'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Droplets className="w-3.5 h-3.5" />
                  Wasser ({filteredMeterPoints.filter(m => m.meter_kind === 'water_cold' || m.meter_kind === 'water_hot').length})
                </button>
                <button
                  onClick={() => setBulkFilterType('gas')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                    bulkFilterType === 'gas'
                      ? 'bg-orange-600 text-white'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Flame className="w-3.5 h-3.5" />
                  Gas ({filteredMeterPoints.filter(m => m.meter_kind === 'gas').length})
                </button>
                <button
                  onClick={() => setBulkFilterType('heat')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                    bulkFilterType === 'heat'
                      ? 'bg-red-600 text-white'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Thermometer className="w-3.5 h-3.5" />
                  Wärme ({filteredMeterPoints.filter(m => m.meter_kind === 'heat').length})
                </button>
              </div>
            </div>

            <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {filteredMeterPoints.filter(meter => bulkFilterType === 'all' || meter.meter_kind === bulkFilterType || (bulkFilterType === 'water_cold' && (meter.meter_kind === 'water_cold' || meter.meter_kind === 'water_hot'))).map((meter) => (
                <div
                  key={meter.id}
                  className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="font-mono text-sm font-semibold">{meter.external_id}</div>
                      <div className="text-xs text-gray-500">{meter.location_description}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${
                        meter.meter_kind === 'electricity' ? 'bg-yellow-100 text-yellow-600' :
                        meter.meter_kind === 'water_cold' || meter.meter_kind === 'water_hot' ? 'bg-blue-100 text-blue-600' :
                        meter.meter_kind === 'gas' ? 'bg-orange-100 text-orange-600' :
                        meter.meter_kind === 'heat' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {meter.meter_kind === 'electricity' && <Zap className="w-5 h-5" />}
                        {(meter.meter_kind === 'water_cold' || meter.meter_kind === 'water_hot') && <Droplets className="w-5 h-5" />}
                        {meter.meter_kind === 'gas' && <Flame className="w-5 h-5" />}
                        {meter.meter_kind === 'heat' && <Thermometer className="w-5 h-5" />}
                        {!['electricity', 'water_cold', 'water_hot', 'gas', 'heat'].includes(meter.meter_kind) && <Gauge className="w-5 h-5" />}
                      </div>
                      {/* Info */}
                      <div className="flex-1">
                        <div className="font-mono text-sm font-semibold">{meter.external_id}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          <span>{meter.meter_kind === 'electricity' ? 'Strom' : 
                                 meter.meter_kind === 'water_cold' ? 'Kaltwasser' :
                                 meter.meter_kind === 'water_hot' ? 'Warmwasser' :
                                 meter.meter_kind === 'gas' ? 'Gas' :
                                 meter.meter_kind === 'heat' ? 'Wärme' : meter.meter_kind}</span>
                          {meter.location_description && (
                            <>
                              <span>•</span>
                              <span>{meter.location_description}</span>
                            </>
                          )}
                          {meter.provider_specific_data?.tenant && (
                            <>
                              <span>•</span>
                              <span>{meter.provider_specific_data.tenant}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Zählerstand eingeben"
                        className={`w-full px-3 py-2 rounded border ${
                          theme === 'dark' ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'
                        }`}
                        onChange={(e) => {
                          setMeterReadings(prev => ({
                            ...prev,
                            [meter.id]: { ...prev[meter.id], newReading: e.target.value }
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowReadingModal(false)}
                className={`px-4 py-2 rounded ${
                  theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleBulkReadingSave}
                disabled={savingReadings}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {savingReadings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Meter Modal */}
      {showMeterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  {editingMeter ? 'Zähler bearbeiten' : 'Zähler hinzufügen'}
                </h3>
                <button 
                  onClick={() => setShowMeterModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Meter Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zählerart <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={meterFormData.meter_kind}
                    onChange={(e) => setMeterFormData({ ...meterFormData, meter_kind: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="electricity">⚡ Strom</option>
                    <option value="water_cold">💧 Wasser (Kalt)</option>
                    <option value="water_hot">💧 Wasser (Warm)</option>
                    <option value="gas">🔥 Gas</option>
                    <option value="heat">🌡️ Wärme</option>
                  </select>
                </div>

                {/* External ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zählernummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={meterFormData.external_id}
                    onChange={(e) => setMeterFormData({ ...meterFormData, external_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="z.B. 12345678"
                  />
                </div>

                {/* Location Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standort
                  </label>
                  <input
                    type="text"
                    value={meterFormData.location_description}
                    onChange={(e) => setMeterFormData({ ...meterFormData, location_description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="z.B. Keller, Wohnung 1, etc."
                  />
                </div>

                {/* Provider Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anbieter
                  </label>
                  <input
                    type="text"
                    value={meterFormData.provider_name}
                    onChange={(e) => setMeterFormData({ ...meterFormData, provider_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="z.B. Techem, MVV, etc."
                  />
                </div>

                {/* Provider Serial Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seriennummer
                  </label>
                  <input
                    type="text"
                    value={meterFormData.provider_serial_number}
                    onChange={(e) => setMeterFormData({ ...meterFormData, provider_serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Seriennummer des Zählers"
                  />
                </div>

                {/* Manufacturer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hersteller
                  </label>
                  <input
                    type="text"
                    value={meterFormData.manufacturer}
                    onChange={(e) => setMeterFormData({ ...meterFormData, manufacturer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Hersteller des Zählers"
                  />
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modell
                  </label>
                  <input
                    type="text"
                    value={meterFormData.model}
                    onChange={(e) => setMeterFormData({ ...meterFormData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Modellbezeichnung"
                  />
                </div>

                {/* Installation Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Installationsdatum
                  </label>
                  <input
                    type="date"
                    value={meterFormData.installation_date}
                    onChange={(e) => setMeterFormData({ ...meterFormData, installation_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Device Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={meterFormData.device_status}
                    onChange={(e) => setMeterFormData({ ...meterFormData, device_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Aktiv</option>
                    <option value="inactive">Inaktiv</option>
                    <option value="maintenance">Wartung</option>
                    <option value="error">Fehler</option>
                  </select>
                </div>

                {/* Additional Info Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-700 mb-3">Zusätzliche Informationen</h4>
                  
                  {/* Unit */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Einheit
                    </label>
                    <input
                      type="text"
                      value={meterFormData.provider_specific_data.unit}
                      onChange={(e) => setMeterFormData({ 
                        ...meterFormData, 
                        provider_specific_data: { 
                          ...meterFormData.provider_specific_data, 
                          unit: e.target.value 
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="z.B. Wohnung 1, Einheit A"
                    />
                  </div>

                  {/* Tenant */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mieter
                    </label>
                    <input
                      type="text"
                      value={meterFormData.provider_specific_data.tenant}
                      onChange={(e) => setMeterFormData({ 
                        ...meterFormData, 
                        provider_specific_data: { 
                          ...meterFormData.provider_specific_data, 
                          tenant: e.target.value 
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Name des Mieters"
                    />
                  </div>

                  {/* Contract Account */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vertragskonto
                    </label>
                    <input
                      type="text"
                      value={meterFormData.provider_specific_data.contract_account}
                      onChange={(e) => setMeterFormData({ 
                        ...meterFormData, 
                        provider_specific_data: { 
                          ...meterFormData.provider_specific_data, 
                          contract_account: e.target.value 
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Vertragskonto-Nummer"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowMeterModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleMeterSave}
                  disabled={savingMeter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {savingMeter ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingMeter ? 'Aktualisieren' : 'Hinzufügen'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeterDashboard;

